/* ──────────────────────────────────────────────────────────────────────────
 * CA Firm Onboarding — short questionnaire that registers a CA firm.
 * Reuses the IPO Navigator onboarding look & feel (scoped `.ipo-ob-root` in
 * IPOWorkspaceOnboarding.css). On submit → POST /api/ca-firm, then the user is
 * taken to their branded /ca-firm-dashboard.
 * ────────────────────────────────────────────────────────────────────────── */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import "./IPOWorkspaceOnboarding.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CAFirmOnboarding = () => {
  const navigate = useNavigate();
  const [firm, setFirm] = useState({
    firm_name: "", icai_frn: "", city: "", email: "", mobile: "",
    poc_name: "", poc_designation: "", website: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setFirm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!firm.firm_name.trim() || !firm.icai_frn.trim()) {
      toast.error("Please add your firm name and ICAI FRN.");
      return;
    }
    if (!firm.email.trim() || !firm.mobile.trim() || !firm.poc_name.trim()) {
      toast.error("Please add firm email, mobile and a point of contact.");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/ca-firm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(firm),
      });
      if (!res.ok) {
        let msg = "Could not register your firm. Please try again.";
        try { const d = await res.json(); if (typeof d.detail === "string") msg = d.detail; } catch { /* ignore */ }
        toast.error(msg);
        setSaving(false);
        return;
      }
      toast.success("Your CA firm portal is ready.");
      navigate("/ca-firm-dashboard");
    } catch {
      toast.error("Could not register your firm. Please try again.");
      setSaving(false);
    }
  };

  return (
    <div className="ipo-ob-root" data-testid="ca-firm-onboarding-page">
      <div className="iob-topbar">
        <div className="iob-brand" onClick={() => navigate("/ipo-role-select")} data-testid="cfo-brand">
          IPO<span>Navigator</span>
        </div>
        <div className="iob-eyebrow">CA Firm Registration</div>
        <div className="iob-back" onClick={() => navigate("/ipo-role-select")} data-testid="cfo-back">
          <i className="ti ti-arrow-left" aria-hidden="true" /> Back
        </div>
      </div>

      <div className="iob-shell">
        <div className="ob">
          <div className="ob-head">
            <div className="ob-kick">Step 1 of 1 · Firm setup</div>
            <div className="ob-h">Register your CA firm</div>
            <div className="ob-sub">
              A few details about your practice and we&apos;ll set up your branded portal to track and
              rank your clients by IPO readiness. You can refine everything later.
            </div>
          </div>

          <div className="ob-card">
            <div className="sec-t"><i className="ti ti-building-bank" aria-hidden="true" /> Firm details</div>

            <div className="fg">
              <label className="fl">Firm name <span className="req">*</span></label>
              <input className="fi" placeholder="e.g. Anand Karkera & Associates LLP" value={firm.firm_name} onChange={(e) => set("firm_name", e.target.value)} data-testid="cfo-firm-name" />
            </div>

            <div className="row2">
              <div className="fg">
                <label className="fl">ICAI Firm Reg. No. (FRN) <span className="req">*</span></label>
                <input className="fi" placeholder="e.g. 118273W" value={firm.icai_frn} onChange={(e) => set("icai_frn", e.target.value)} data-testid="cfo-icai-frn" />
              </div>
              <div className="fg">
                <label className="fl">City / location</label>
                <input className="fi" placeholder="e.g. Mumbai" value={firm.city} onChange={(e) => set("city", e.target.value)} data-testid="cfo-city" />
              </div>
            </div>

            <div className="row2">
              <div className="fg">
                <label className="fl">Firm email <span className="req">*</span></label>
                <input className="fi" placeholder="firm@example.com" value={firm.email} onChange={(e) => set("email", e.target.value)} data-testid="cfo-email" />
              </div>
              <div className="fg">
                <label className="fl">Firm mobile <span className="req">*</span></label>
                <input className="fi" placeholder="+91" value={firm.mobile} onChange={(e) => set("mobile", e.target.value)} data-testid="cfo-mobile" />
              </div>
            </div>

            <div className="row2">
              <div className="fg">
                <label className="fl">Point of contact <span className="req">*</span></label>
                <input className="fi" placeholder="Full name" value={firm.poc_name} onChange={(e) => set("poc_name", e.target.value)} data-testid="cfo-poc-name" />
              </div>
              <div className="fg">
                <label className="fl">POC designation</label>
                <input className="fi" placeholder="e.g. Managing Partner" value={firm.poc_designation} onChange={(e) => set("poc_designation", e.target.value)} data-testid="cfo-poc-designation" />
              </div>
            </div>

            <div className="fg" style={{ marginTop: 4 }}>
              <label className="fl">Firm website</label>
              <input className="fi" placeholder="https://" value={firm.website} onChange={(e) => set("website", e.target.value)} data-testid="cfo-website" />
            </div>

            <div className="ob-foot">
              <span className="score">All key details help us brand your portal.</span>
              <button className="submit" onClick={submit} disabled={saving} data-testid="cfo-submit">
                {saving ? "Submitting…" : "Review & Submit →"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CAFirmOnboarding;
